import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, type OnInit, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AzureAuthService } from '@dtis/angular';
import { TranslocoModule } from '@jsverse/transloco';
import { select } from '@ngxs/store';
import { catchError, of, take } from 'rxjs';
import { SettingsSelector } from '../../app/states/settings/settings.selectors';
import { FeedbackApiService } from '../../services/api/feedback.service';
import type { FeedbackDTO, FeedbackResponseDTO } from '../../services/api/models/FeedbackModel';

@Component({
  selector: 'dtis-support-request-dialog',
  imports: [
    CommonModule,
    MatIconModule,
    TranslocoModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatListModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  template: `
    @if (isLoading()) {
    <div class="flex items-center justify-center p-6">
      <mat-spinner diameter="40"></mat-spinner>
    </div>
    } @else {
    <h2 mat-dialog-title>
      {{ 'support-request-dialog-title' | transloco }}
    </h2>
    <mat-dialog-content>
      <div class="flex w-full flex-col gap-4">
        <div class="flex flex-col items-center gap-4 rounded-lg p-6">
      
      <p class="text-onSurfaceVariant text-xs">
        {{ 'support-request-dialog-body-text' | transloco }}
      </p>
        <!-- Debug mode input -->
      @if (debug$()) {
      <input type="text" [value]="currentFeedback?.identifier" />
      }
      <!-- Message input -->
      <mat-form-field class="w-full"  appearance="outline">
        <mat-label>
          {{ 'support-request-your-message' | transloco }}
        </mat-label>
        <textarea matInput type="textarea" [formControl]="messageFC"></textarea>
      </mat-form-field>
      <!-- Options list -->
      <mat-selection-list class="w-full" #options>
        <!-- Screenshot option -->
        <mat-list-option #optScreenshot>
          <div matListItemTitle>{{ 'screenshot-options' | transloco }}</div>
        </mat-list-option>
        @if (options.selectedOptions.isSelected(optScreenshot)) {
        <div class="m-2 flex flex-col gap-2">
          <img class="h-80 w-auto" [src]="dialogData.screenshot" alt="" />
          <hr />
        </div>
        }
        <!-- Debug file option -->
        <mat-list-option #optDebug>
          <div matListItemTitle>{{ 'support-request.debug-title' | transloco }}</div>
        </mat-list-option>
      </mat-selection-list>
        </div>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions class="flex! w-full justify-end! gap-2">
      <button mat-button mat-dialog-close>{{ 'cancel' | transloco }}</button>
      <button
        mat-flat-button type="button"
        [disabled]="
          !messageFC.valid ||
          (options.selectedOptions.isSelected(optScreenshot) &&
            !screenshotUploadFinished &&
            screenShotUploading)
        "
        (click)="
          createFeedback(
            messageFC.value || '',
            options.selectedOptions.isSelected(optScreenshot),
            options.selectedOptions.isSelected(optDebug)
          )
        "
      >
        {{ 'submit' | transloco }}
      </button>
    </mat-dialog-actions>
    }
  `,
})
export class SupportRequestDialogComponent implements OnInit {
  // Inject services
  private readonly feedbackAPI = inject(FeedbackApiService);
  readonly dialogData = inject(MAT_DIALOG_DATA);
  private readonly auth = inject(AzureAuthService);
  private readonly snack = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly cdr = inject(ChangeDetectorRef);

  /**
   * Loading state signal for initial dialog loading
   */
  isLoading = signal(true);

  /**
   * Form control for the message textarea with required validation
   */
  messageFC = new FormControl('', [Validators.required]);

  /**
   * Observable to determine if debug mode is enabled.
   * Used to conditionally display debug-related UI elements.
   */
  debug$ = select(SettingsSelector.getDebugMode);

  /**
   * Holds the current feedback data, including the identifier
   * and other metadata for the feedback session.
   */
  currentFeedback?: FeedbackDTO;

  /**
   * Indicates whether a screenshot is currently being uploaded.
   * Used to disable UI elements during the upload process.
   */
  screenShotUploading: boolean = false;

  /**
   * Indicates whether the screenshot upload has been completed successfully.
   * Used to update the UI and enable further actions.
   */
  screenshotUploadFinished: boolean = false;

  /**
   * Stores the list of files added by the user during the session.
   * Primarily used for displaying the file names in the UI.
   */
  addedFiles: any[] = [];

  ngOnInit() {
    // Show loading spinner for 200ms before displaying dialog content
    setTimeout(() => {
      this.isLoading.set(false);
    }, 200);
  }

  /**
   * Upload debug file
   * @param e - Whether the debug option is selected
   */
  uploadDebugFile(e: boolean, feedback: FeedbackDTO) {
    if (!feedback?.identifier || !e) {
      return;
    }
    const file = new File([JSON.stringify(localStorage['@@STATE'])], 'debug.json', { type: 'application/json' });
    this.feedbackAPI.uploadFile({ id: feedback.identifier, file }).pipe(take(1)).subscribe();
  }

  /**
   * Upload screenshot
   * @param event - Optional event containing the file
   */
  async uploadScreenshotTo(feedback: FeedbackResponseDTO) {
    this.screenShotUploading = true;

    const file: any = await (await fetch(this.dialogData.screenshot)).blob();
    this.feedbackAPI
      .uploadScreenshot({ id: feedback.identifier, file })
      .pipe(
        take(1),
        catchError(() => of(false))
      )
      .subscribe(() => {
        this.screenShotUploading = false;
        this.screenshotUploadFinished = true;
        this.cdr.markForCheck();
      });
  }
  /**
   *
   * @param description
   * @param screenshot
   * @param file
   * @returns
   */
  createFeedback(description: string, screenshot: boolean = false, file: boolean = false) {
    const mail = this.auth.handleAccount()?.username;
    if (!mail) {
      this.snack.open('Error figure out the recipient', 'Close', {
        duration: 5000,
      });
      throw new Error('Could not determine the recipient');
    }
    this.feedbackAPI
      .createFeedback({ description })
      .pipe(
        catchError(() => {
          this.snack.open('Error sending support request', 'Close', {
            duration: 5000,
          });
          return of();
        })
      )
      .subscribe((feedback) => {
        if (!feedback) {
          return;
        }

        if (screenshot) {
          this.uploadScreenshotTo(feedback);
        }
        if (file) {
          this.uploadDebugFile(true, feedback);
        }
        this.dialog.closeAll();
        this.snack.open('Support request submitted successfully', undefined, {
          duration: 3000,
        });
      });
  }
}
